/**
 * Pure Markdown tokenizer for the QuestionBox's live decoration. Deliberately
 * free of DOM/CSS imports so it can be unit-tested in isolation.
 *
 * It supports a small, single-line inline subset plus line-start block markers.
 * Crucially, *incomplete* constructs never match — `**foo` stays literal text
 * until the closing `**` arrives — which is what makes the editor decoration
 * feel like "reveal as you complete it".
 */

/** ASCII punctuation that a leading backslash may escape (`\*`, `\_`, …). */
const ESCAPABLE = new Set("\\`*_{}[]()#+-.!~>".split(""));

// --- Inline -------------------------------------------------------------

export type MdInlineKind = "strong" | "em" | "code" | "strike";

export type MdToken =
	| { kind: "text"; value: string }
	| { kind: "esc"; mark: string; value: string }
	| {
			kind: MdInlineKind;
			open: string;
			close: string;
			children: MdToken[];
	  };

/** Two-char delimiters, tried before their single-char counterparts. */
const DOUBLE_DELIMS: { marker: string; kind: MdInlineKind }[] = [
	{ marker: "**", kind: "strong" },
	{ marker: "__", kind: "strong" },
	{ marker: "~~", kind: "strike" },
];
/** Single-char delimiters. */
const SINGLE_DELIMS: { marker: string; kind: MdInlineKind }[] = [
	{ marker: "*", kind: "em" },
	{ marker: "_", kind: "em" },
];

const isWhitespace = (ch: string | undefined): boolean =>
	ch === undefined || /\s/.test(ch);

/**
 * Finds a closing `marker` for an emphasis/strike run opened at `openEnd`.
 * The run must be non-empty and neither start nor end with whitespace (so
 * `** foo**` and `**foo **` stay literal, matching typical Markdown flanking
 * rules). Returns the index where the closer starts, or -1.
 */
function findEmphasisClose(
	text: string,
	marker: string,
	openEnd: number,
): number {
	if (isWhitespace(text[openEnd])) return -1;
	let i = openEnd;
	while (i < text.length) {
		const at = text.indexOf(marker, i);
		if (at < 0) return -1;
		if (at === openEnd) {
			// Empty run (e.g. `****`); skip and keep looking.
			i = at + marker.length;
			continue;
		}
		if (isWhitespace(text[at - 1])) {
			i = at + marker.length;
			continue;
		}
		return at;
	}
	return -1;
}

/** Attempts to match a delimited construct starting at `i`. */
function matchDelimiter(
	text: string,
	i: number,
): { marker: string; kind: MdInlineKind; end: number; inner: string } | null {
	// Inline code first: its content is literal, so markers inside are ignored.
	if (text[i] === "`") {
		const close = text.indexOf("`", i + 1);
		if (close > i + 1) {
			return {
				marker: "`",
				kind: "code",
				end: close + 1,
				inner: text.slice(i + 1, close),
			};
		}
		return null;
	}

	for (const { marker, kind } of DOUBLE_DELIMS) {
		if (text.startsWith(marker, i)) {
			const close = findEmphasisClose(text, marker, i + marker.length);
			if (close < 0) continue;
			return {
				marker,
				kind,
				end: close + marker.length,
				inner: text.slice(i + marker.length, close),
			};
		}
	}
	for (const { marker, kind } of SINGLE_DELIMS) {
		if (text[i] === marker) {
			// Don't treat a `*`/`_` that's part of a longer run (e.g. the `**` in
			// `** foo**`) as a single-char opener; the double-delimiter pass already
			// had its chance, so leave the run literal.
			if (text[i - 1] === marker || text[i + 1] === marker) continue;
			const close = findEmphasisClose(text, marker, i + 1);
			if (close < 0) continue;
			return {
				marker,
				kind,
				end: close + 1,
				inner: text.slice(i + 1, close),
			};
		}
	}
	return null;
}

/**
 * Tokenises a single line of inline Markdown for the supported subset
 * (`**bold**`/`__bold__`, `*italic*`/`_italic_`, `` `code` ``, `~~strike~~`).
 * Incomplete constructs fall through as literal text. Escapes (`\*`) are kept
 * as a hidden backslash + literal char so the source round-trips exactly.
 */
export function tokenizeInline(text: string): MdToken[] {
	const tokens: MdToken[] = [];
	let buffer = "";
	const flush = () => {
		if (buffer.length > 0) {
			tokens.push({ kind: "text", value: buffer });
			buffer = "";
		}
	};

	let i = 0;
	while (i < text.length) {
		const ch = text[i];
		if (ch === "\\" && i + 1 < text.length && ESCAPABLE.has(text[i + 1])) {
			flush();
			tokens.push({ kind: "esc", mark: "\\", value: text[i + 1] });
			i += 2;
			continue;
		}

		const match = matchDelimiter(text, i);
		if (match) {
			flush();
			const children =
				match.kind === "code"
					? [{ kind: "text", value: match.inner } as MdToken]
					: tokenizeInline(match.inner);
			tokens.push({
				kind: match.kind,
				open: match.marker,
				close: match.marker,
				children,
			});
			i = match.end;
			continue;
		}

		buffer += ch;
		i += 1;
	}
	flush();
	return tokens;
}
